import { useContext, useEffect, useRef, useState, type FormEvent } from "react";

import { ProjectContext } from "../app/projectContext";
import type { SldElements } from "../services/sldElements";

import { pvRunCreateRequestSchema, type PVRunCreateRequest } from "../services/pvSizing";
import {
  pvBatteryConfigurationSchema,
  pvInverterRedundancyModeSchema,
  pvPhaseConfigurationSchema,
  pvSystemTypeSchema,
  type PVInverterRedundancyMode,
} from "../services/pvSizingContract";
import { loadScenarioSchema } from "../services/loadDemandContract";
import { useStudyDraft } from "../hooks/useStudyDraft";
import { deriveStandbyUnits } from "../utils/sourceSizing";
import { describeValidationIssue } from "../utils/validationMessages";
import { SCENARIO_LABELS } from "./LoadRow";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker, sldElementsOf } from "./SldElementPicker";
import {
  PV_BATTERY_CONFIGURATION_LABELS,
  PV_DG_COEXISTENCE_CHOICES,
  PV_DG_COEXISTENCE_LABELS,
  PV_PHASE_CONFIGURATION_LABELS,
  PV_REDUNDANCY_HINTS,
  PV_REDUNDANCY_LABELS,
  PV_STUDY_DRAFT_MODULE,
  PV_STUDY_LABELS,
  PV_SYSTEM_TYPE_LABELS,
  buildPVRunPayload,
  createInitialPVStudyDraft,
  createPVRatingDraft,
  pvStudyDraftSchema,
  seedPVRatingIdCounter,
  type PVDGCoexistenceChoice,
  type PVRatingDraft,
  type PVStudyDraft,
} from "./pvStudyDraft";

/** The first option of every choice: nothing is chosen for the engineer (A19 (g), (j)). */
const CHOOSE = "Choose…";

export const MODULE_HINT = "From the module datasheet of the project design basis (STC values).";

export const TEMPERATURES_HINT =
  "Required. Take them from the site design basis; they are never assumed.";

export const TARGET_RATIO_HINT =
  "Required design-basis value: array kWp divided by inverter AC kW. No limit is applied.";

export const INVERTER_HINT =
  "From the inverter datasheet; it applies to every rating in the schedule.";

export const AC_EFFICIENCY_HINT = "Leave blank if not yet known (flagged for review).";

export const DERATING_HINT = "From the inverter's certified derating data.";

export const BATTERY_HINT = "Battery sizing is not evaluated.";

export const DESCRIPTORS_NOTE = "Recorded with the study; they do not change any calculated value.";

type PVStudyFormProps = {
  disabled?: boolean;
  onSubmit: (payload: PVRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): a PV source
  // is picked from it. Nothing of the sizing is prefilled - the study selects the rating,
  // and the result is compared with the diagram afterwards (A28 (d)).
  sldElements?: SldElements | null | undefined;
};

// The Solar PV sizing study (EOS-03d). The form owns the draft. The redundant
// inverter count is derived from the redundancy mode rather than typed, so the
// rule the backend enforces cannot be broken here. The redundancy mode, the DG
// question and the four descriptors start unchosen: the backend imposes no
// default (A19 (g), (j), (k)).
//
// The draft is kept under its own module key, "pv-sizing", next to the
// Transformer, DG and UPS forms' keys: all four belong to EOS-03, but no form
// ever reads, restores or deletes another's draft.
//
// The payload carries no project revision: the page adds the selected revision
// when it calls the API, exactly as the other study pages do.
export function PVStudyForm({ disabled = false, onSubmit, sldElements }: PVStudyFormProps) {
  // Tolerant of a missing provider (no project, no revision), like the fault form.
  const revisionId = useContext(ProjectContext)?.activeRevisionId() ?? null;
  // The entries outlive a reload, Back and the sidebar for as long as the tab is open.
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<PVStudyDraft>({
      module: PV_STUDY_DRAFT_MODULE,
      createInitial: createInitialPVStudyDraft,
      schema: pvStudyDraftSchema,
      revisionId,
    });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [ratingToFocus, setRatingToFocus] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // A new entry sits among several fieldsets, so the cursor goes to it.
  useEffect(() => {
    if (ratingToFocus === null) {
      return;
    }
    formRef.current
      ?.querySelector<HTMLInputElement>(`input[name="rating-${ratingToFocus}"]`)
      ?.focus();
    setRatingToFocus(null);
  }, [ratingToFocus]);

  // Every edit keeps the derived redundant count in step with what it is made of.
  function updateStudy(patch: Partial<PVStudyDraft>) {
    setDraft((current) => {
      const next = { ...current, ...patch };
      return {
        ...next,
        redundantInverters: deriveStandbyUnits(next.redundancyMode, next.dutyInverters),
      };
    });
  }

  function updateRating(id: string, value: string) {
    setDraft((current) => ({
      ...current,
      unitRatings: current.unitRatings.map((rating) =>
        rating.id === id ? { ...rating, value } : rating,
      ),
    }));
  }

  function addRating() {
    // A restored draft brings ids the counter of this page load has not handed out yet.
    seedPVRatingIdCounter(draft.unitRatings.map((rating) => rating.id));
    const rating: PVRatingDraft = createPVRatingDraft();
    setDraft((current) => ({ ...current, unitRatings: [...current.unitRatings, rating] }));
    setRatingToFocus(rating.id);
  }

  function removeRating(id: string) {
    setDraft((current) => ({
      ...current,
      unitRatings: current.unitRatings.filter((rating) => rating.id !== id),
    }));
  }

  // Empties the form only; a result already on the page stays until "Clear results".
  function clearForm() {
    setValidationError(null);
    clear();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = pvRunCreateRequestSchema.safeParse(buildPVRunPayload(draft));

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(
        issue ? describeValidationIssue(issue, PV_STUDY_LABELS) : "Review the PV sizing inputs.",
      );
      return;
    }

    await onSubmit(parsed.data);
  }

  const redundancyMode = draft.redundancyMode as PVInverterRedundancyMode | "";

  const textField = (
    label: string,
    field: keyof PVStudyDraft,
    name: string,
    inputMode: "decimal" | "numeric" = "decimal",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={name}
        value={draft[field] as string}
        onChange={(event) => updateStudy({ [field]: event.target.value } as Partial<PVStudyDraft>)}
      />
    </label>
  );

  // A select with "Choose…" first and the contract's values after it.
  const choiceField = <T extends string>(
    label: string,
    field: keyof PVStudyDraft,
    name: string,
    options: readonly T[],
    labels: Record<T, string>,
  ) => (
    <label>
      {label}
      <select
        name={name}
        value={draft[field] as string}
        onChange={(event) => updateStudy({ [field]: event.target.value } as Partial<PVStudyDraft>)}
      >
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <form aria-label="PV sizing inputs" ref={formRef} onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice
          onKeep={keepForThisRevision}
          onClear={clearForm}
          disabled={disabled}
        />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>

        <label>
          Study code
          <input
            name="studyCode"
            value={draft.code}
            onChange={(event) => updateStudy({ code: event.target.value })}
          />
        </label>

        <label>
          Study name
          <input
            name="studyName"
            value={draft.name}
            onChange={(event) => updateStudy({ name: event.target.value })}
          />
        </label>

        {sldElementsOf(sldElements, ["source"], ["PV"]).length > 0 ? (
          <SldElementPicker
            label="Source from the diagram"
            name="sldElementCode"
            value={draft.sldElementCode}
            onChange={(sldElementCode) => updateStudy({ sldElementCode })}
            categories={["source"]}
            kinds={["PV"]}
            elements={sldElements}
          />
        ) : null}

        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">Australia / New Zealand (AS/NZS 3000) - reference data pending</option>
          </select>
        </label>
        <p>
          The sizing itself carries no profile-dependent value; the profile is recorded with
          the run and must match the design basis of the project revision.
        </p>

        <label>
          Study notes
          <input
            name="studyNotes"
            value={draft.notes}
            onChange={(event) => updateStudy({ notes: event.target.value })}
          />
        </label>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>AC requirement</legend>
        {textField("Required AC output (kW)", "requiredAcOutputKw", "requiredAcOutputKw")}
        {textField(
          "AC efficiency (inverter to delivery point)",
          "acEfficiencyFactor",
          "acEfficiencyFactor",
        )}
        <p data-ac-efficiency-hint="true">{AC_EFFICIENCY_HINT}</p>
        {textField("Target DC/AC ratio", "targetDcAcRatio", "targetDcAcRatio")}
        <p data-target-ratio-hint="true">{TARGET_RATIO_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>PV module data</legend>
        {textField("Module rated power (Wp)", "moduleRatedPowerWp", "moduleRatedPowerWp")}
        {textField(
          "Module open-circuit voltage Voc (V)",
          "moduleOpenCircuitVoltageV",
          "moduleOpenCircuitVoltageV",
        )}
        {textField(
          "Module maximum-power voltage Vmp (V)",
          "moduleMaximumPowerVoltageV",
          "moduleMaximumPowerVoltageV",
        )}
        {textField(
          "Module short-circuit current Isc (A)",
          "moduleShortCircuitCurrentA",
          "moduleShortCircuitCurrentA",
        )}
        {textField(
          "Voc temperature coefficient (%/°C)",
          "temperatureCoefficientVocPercentPerC",
          "temperatureCoefficientVocPercentPerC",
        )}
        {textField(
          "Vmp temperature coefficient (%/°C)",
          "temperatureCoefficientVmpPercentPerC",
          "temperatureCoefficientVmpPercentPerC",
        )}
        <p data-module-hint="true">{MODULE_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Site temperatures</legend>
        {textField(
          "Minimum design temperature (°C)",
          "minimumDesignTemperatureC",
          "minimumDesignTemperatureC",
        )}
        {textField(
          "Maximum cell temperature (°C)",
          "maximumCellTemperatureC",
          "maximumCellTemperatureC",
        )}
        <p data-temperatures-hint="true">{TEMPERATURES_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Inverter data</legend>
        {textField(
          "Inverter maximum DC voltage (V)",
          "inverterMaxDcVoltageV",
          "inverterMaxDcVoltageV",
        )}
        {textField(
          "MPPT minimum voltage (V)",
          "inverterMpptMinVoltageV",
          "inverterMpptMinVoltageV",
        )}
        {textField(
          "MPPT maximum voltage (V)",
          "inverterMpptMaxVoltageV",
          "inverterMpptMaxVoltageV",
        )}
        {textField(
          "Maximum input current per MPPT (A)",
          "inverterMaxInputCurrentPerMpptA",
          "inverterMaxInputCurrentPerMpptA",
        )}
        {textField("MPPT inputs per inverter", "mpptCount", "mpptCount", "numeric")}
        {textField(
          "Maximum strings per MPPT",
          "maximumStringsPerMppt",
          "maximumStringsPerMppt",
          "numeric",
        )}
        <p data-inverter-hint="true">{INVERTER_HINT}</p>
        <fieldset disabled={disabled}>
          <legend>Inverter ratings</legend>
          {draft.unitRatings.map((rating, index) => (
            <div key={rating.id} data-rating-entry={rating.id}>
              <label>
                Inverter rating {index + 1}
                <input
                  inputMode="decimal"
                  name={`rating-${rating.id}`}
                  value={rating.value}
                  onChange={(event) => updateRating(rating.id, event.target.value)}
                />
              </label>
              {draft.unitRatings.length > 1 ? (
                <button type="button" onClick={() => removeRating(rating.id)}>
                  Remove rating {index + 1}
                </button>
              ) : null}
            </div>
          ))}
          <button type="button" onClick={addRating}>
            Add rating
          </button>
          <p data-ratings-hint="true">
            Enter the inverter ratings from the project design basis in ascending order.
          </p>
        </fieldset>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Factors</legend>
        {textField("Future growth factor", "futureGrowthFactor", "futureGrowthFactor")}
        {textField("Design margin factor", "designMarginFactor", "designMarginFactor")}
        {textField("Ambient derating factor", "ambientDeratingFactor", "ambientDeratingFactor")}
        {textField("Altitude derating factor", "altitudeDeratingFactor", "altitudeDeratingFactor")}
        <p data-derating-hint="true">{DERATING_HINT}</p>
        <p data-not-established-hint="true">
          Leave a factor blank if it is not established — the result will be marked
          Engineering review required.
        </p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Inverters and redundancy</legend>
        {textField("Duty inverters", "dutyInverters", "dutyInverters", "numeric")}
        {choiceField(
          "Redundancy mode",
          "redundancyMode",
          "redundancyMode",
          pvInverterRedundancyModeSchema.options,
          PV_REDUNDANCY_LABELS,
        )}
        <label>
          Redundant inverters
          <input name="redundantInverters" value={draft.redundantInverters} readOnly />
        </label>
        {redundancyMode === "" ? null : (
          <p data-redundant-hint="true">{PV_REDUNDANCY_HINTS[redundancyMode]}</p>
        )}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Grid and DG</legend>
        {textField("Export limit (kW)", "exportLimitKw", "exportLimitKw")}
        <label>
          Operates alongside a DG
          <select
            name="dgCoexistence"
            value={draft.dgCoexistence}
            onChange={(event) =>
              updateStudy({ dgCoexistence: event.target.value as PVDGCoexistenceChoice })
            }
          >
            {PV_DG_COEXISTENCE_CHOICES.map((choice) => (
              <option key={choice} value={choice}>
                {PV_DG_COEXISTENCE_LABELS[choice]}
              </option>
            ))}
          </select>
        </label>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Descriptors (optional)</legend>
        <p data-descriptors-note="true">{DESCRIPTORS_NOTE}</p>
        {choiceField(
          "System type",
          "systemType",
          "systemType",
          pvSystemTypeSchema.options,
          PV_SYSTEM_TYPE_LABELS,
        )}
        {choiceField(
          "Phase configuration",
          "phaseConfiguration",
          "phaseConfiguration",
          pvPhaseConfigurationSchema.options,
          PV_PHASE_CONFIGURATION_LABELS,
        )}
        {choiceField(
          "Battery configuration",
          "batteryConfiguration",
          "batteryConfiguration",
          pvBatteryConfigurationSchema.options,
          PV_BATTERY_CONFIGURATION_LABELS,
        )}
        <p data-battery-hint="true">{BATTERY_HINT}</p>
        {choiceField(
          "Scenario",
          "scenario",
          "scenario",
          loadScenarioSchema.options,
          SCENARIO_LABELS,
        )}
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Calculate PV size
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}
